import { InputAdornment, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Briefcase02Icon } from '@/components/icons/Briefcase02Icon';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { PossibleRelationshipOwnersTypeahead } from '@/modules/household/components/PossibleRelationshipOwnersTypeahead/PossibleRelationshipOwnersTypeahead';
import { TeamsTypeahead } from '@/modules/teams/components/TeamsTypeahead/TeamsTypeahead';
import { HouseholdBillableKind, UpdateHouseholdInput } from '@/types/schema';

import {
  UpdateAdvisorClientMutationVariables,
  useUpdateAdvisorClientMutation,
} from './graphql/Update.generated';
import { useExistingHouseholdFormData } from './hooks/useExistingValues';
import { ManageHouseholdAccessForm } from './ManageHouseholdAccessForm/ManageHouseholdAccessForm';
import {
  getEmptyClientProfileValue,
  UpdateHouseholdFormPaths,
  UpdateHouseholdFormShape,
} from './types';

export const getDefaultValues = (): UpdateHouseholdFormShape => ({
  primaryRelationshipOwnerId: '',
  subBrandId: '',
  teamIds: [],
  _initialTeamIds: [],
  profiles: [getEmptyClientProfileValue({ relationshipOptions: [] })],
  isProspect: false,
});

interface UpdateClientProps {
  householdId: string;
}

export function UpdateClient({ householdId }: UpdateClientProps) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const formMethods = useForm<UpdateHouseholdFormShape>({
    defaultValues: getDefaultValues(),
  });
  const { reset, control, handleSubmit } = formMethods;
  const canEdit = useCanEditHouseholdData();
  const canEditRelationshipOwner = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_REASSIGN_RELATIONSHIP_OWNER
  );
  const canUpdateSubBrand = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_UPDATE_SUB_BRAND
  );
  const { createSuccessFeedback } = useFeedback();

  const [updateClient, { loading }] = useUpdateAdvisorClientMutation({
    onError: (err) => {
      reportError('failed to load household data', err);
      showFeedback(
        'We failed to load data for this client. Please refresh the page to try again.'
      );
    },
    onCompleted: createSuccessFeedback('Client updates saved successfully'),
    // we need to evict here to purge/repopulate the cache for sub-brand-specific branding and disclaimers
    update: (cache) => {
      cache.evict({
        id: cache.identify({
          __typename: 'Household',
          id: householdId,
        }),
      });
    },
  });

  const { formData: existingValues, subBrandOptions } =
    useExistingHouseholdFormData({ householdId });

  useEffect(() => {
    if (!existingValues) return;
    reset(existingValues);
  }, [existingValues, reset]);

  const showSubBrandOptions = useMemo(
    () => subBrandOptions.length > 0,
    [subBrandOptions.length]
  );

  const getSaveButtonWidth = () => {
    if (showSubBrandOptions) return 3;
    return 4;
  };

  const getVariablesFromValues = useCallback(
    (
      values: UpdateHouseholdFormShape
    ): UpdateAdvisorClientMutationVariables => {
      const input: UpdateHouseholdInput = {
        primaryRelationshipOwnerID: values.primaryRelationshipOwnerId,
        billableKind: values.isProspect
          ? HouseholdBillableKind.Prospect
          : HouseholdBillableKind.Billable,
      };

      if (values.subBrandId) {
        input.subBrandID = values.subBrandId;
      } else {
        input.clearSubBrand = true;
      }

      // Handle team assignments
      const originalTeamIds = values._initialTeamIds;
      const newTeamIds = values.teamIds;
      const teamsToAdd = newTeamIds.filter(
        (id) => !originalTeamIds.includes(id)
      );
      const teamsToRemove = originalTeamIds.filter(
        (id) => !newTeamIds.includes(id)
      );

      if (teamsToAdd.length > 0) {
        input.addAssignedTeamIDs = teamsToAdd;
      }

      if (teamsToRemove.length > 0) {
        input.removeAssignedTeamIDs = teamsToRemove;
      }

      return { updateHouseholdId: householdId, input };
    },
    [householdId]
  );

  const onSubmit = handleSubmit((values) => {
    const variables = getVariablesFromValues(values);
    return updateClient({
      variables,
    });
  });

  return (
    <FormProvider {...formMethods}>
      <Stack spacing={2} direction="column">
        <Stack direction="row" spacing={2} m={3}>
          <Card variant="filled-outline" sx={{ p: 2, flexGrow: 1 }}>
            <Stack component="form" noValidate>
              <FormLayoutRow>
                <FormLayoutItem>
                  <Stack direction="column">
                    <Typography variant="title-l">Key details</Typography>
                  </Stack>
                </FormLayoutItem>
              </FormLayoutRow>
              <FormLayoutRow sx={{ alignItems: 'bottom' }}>
                <FormLayoutItem width={showSubBrandOptions ? 3 : 5}>
                  <PossibleRelationshipOwnersTypeahead
                    label="Relationship owner"
                    required={true}
                    fieldName={
                      `primaryRelationshipOwnerId` as const satisfies UpdateHouseholdFormPaths
                    }
                    disabled={!canEditRelationshipOwner}
                    control={control}
                  />
                </FormLayoutItem>
                <FormLayoutItem width={showSubBrandOptions ? 3 : 5}>
                  <TeamsTypeahead
                    label="Teams"
                    fieldName={
                      `teamIds` as const satisfies UpdateHouseholdFormPaths
                    }
                    control={control}
                    disabled={!canEdit}
                    disableSCIMManagedTeams={false}
                  />
                </FormLayoutItem>
                {showSubBrandOptions && (
                  <FormLayoutItem width={3}>
                    <FormAwareSelectInput
                      label="Sub-brand"
                      disabled={!canUpdateSubBrand}
                      fieldName={
                        `subBrandId` as const satisfies UpdateHouseholdFormPaths
                      }
                      options={subBrandOptions}
                      control={control}
                      showEmptyValue={false}
                      startAdornment={
                        <InputAdornment position="start">
                          <Briefcase02Icon size={16} />
                        </InputAdornment>
                      }
                    />
                  </FormLayoutItem>
                )}
                <FormLayoutItem
                  width={getSaveButtonWidth()}
                  sx={{ display: 'flex', alignItems: 'end' }}
                >
                  <Button
                    disabled={!canEditRelationshipOwner && !canUpdateSubBrand}
                    loading={loading}
                    variant="primary"
                    size="sm"
                    type="button"
                    onClick={onSubmit}
                  >
                    Save
                  </Button>
                </FormLayoutItem>
              </FormLayoutRow>
              <FormLayoutRow>
                <FormLayoutItem>
                  <FormAwareSwitch
                    label="This household is a prospect"
                    labelPosition="right"
                    fieldName={
                      `isProspect` as const satisfies UpdateHouseholdFormPaths
                    }
                    control={control}
                    disabled={!canEdit}
                  />
                </FormLayoutItem>
              </FormLayoutRow>
            </Stack>
          </Card>
        </Stack>
        <ManageHouseholdAccessForm householdId={householdId} />
      </Stack>
    </FormProvider>
  );
}
